import { states } from 'constants/states';
import { countries as Countries } from 'countries-list';
import twoToThree from 'countries-list/dist/countries2to3.json';
import { isUSA } from 'helpers/address';
import { firstValidDate } from 'helpers/dates';
import { toNumber } from 'lodash';
import { DateTime } from 'luxon';
import * as yup from 'yup';

/*****************
 * boolean
 *****************/
yup.addMethod(yup.string, 'boolean', function validateBoolean() {
    return this.trim().oneOf(['true', 'false', '1', '0', 'yes', 'no', 'on', 'off']);
});

/*****************
 * date
 *****************/
interface DateTestOptions {
    before?: boolean;
    allowTime?: boolean;
}

type DateTestResults = [valid: true, reason: null] | [valid: false, reason: 'invalid' | 'future'];

const dateTest = (
    value: string | null,
    { before = false, allowTime = true }: DateTestOptions,
    timezone?: string,
): DateTestResults => {
    if (!value) {
        return [true, null];
    }

    const date = firstValidDate(value, { allowTime, timezone });

    if (!date) {
        return [false, 'invalid'];
    }

    if (before && date > DateTime.local({ zone: timezone })) {
        return [false, 'future'];
    }

    return [true, null];
};

yup.addMethod(yup.string, 'date', function validateDate(options: DateTestOptions) {
    return this.test(
        'is-date',
        ({ label }) => {
            if (options?.before) {
                return `${label} is not a valid date or is not in the past`;
            }

            return `${label} is not a valid date`;
        },
        (value, context) => {
            const timezone = context.from ? context.from[0].value.timezone : undefined;

            return dateTest(value ?? null, options, timezone)[0];
        },
    );
});

/*****************
 * alphanum
 *****************/
yup.addMethod(yup.string, 'alphanum', function validateAlphanum() {
    return this.matches(/^[a-zA-Z0-9]+$/, '${label} can only contain letters and numbers');
});

/*****************
 * alphadash
 *****************/
yup.addMethod(yup.string, 'alphadash', function validateAlphadash() {
    return this.matches(
        /^[a-zA-Z0-9_-]+$/,
        '${label} can only contain letters, numbers, dashes, and underscores',
    );
});

/*****************
 * toNumber
 *****************/
const toNumberResult = (value: any, schema: yup.NumberSchema): yup.ValidationError | null => {
    try {
        schema.validateSync(value);
    } catch (e) {
        return e as yup.ValidationError;
    }

    return null;
};

yup.addMethod(
    yup.string,
    'asNumber',
    function validateNumeric(schemaFn: (schema: yup.NumberSchema) => yup.NumberSchema) {
        const outerLabel = this.describe().label;

        const numberSchema = yup.number();

        const schema = schemaFn(outerLabel ? numberSchema.label(outerLabel) : numberSchema);

        return this.test(
            'is-numeric',
            ({ value, label }) => {
                if (value === null) {
                    return toNumberResult(value, schema)?.errors[0];
                }

                const num = toNumber(value.replaceAll('$', ''));

                const isNumeric = ![Infinity, -Infinity, NaN].includes(num);

                if (!isNumeric) {
                    return `${label} cannot be interpreted as a number`;
                }

                return toNumberResult(num, schema)?.errors[0];
            },
            value => {
                if (value === null || typeof value === 'undefined') {
                    return toNumberResult(value, schema) === null;
                }

                const num = toNumber(value.replaceAll(/[$,]/g, ''));

                const isNumeric = ![Infinity, -Infinity, NaN].includes(num);

                if (!isNumeric) {
                    return false;
                }

                return toNumberResult(num, schema) === null;
            },
        );
    },
);

/*****************
 * country
 *****************/
const alpha2List: string[] = [];
const alpha3List: string[] = [];
const names: string[] = ['united states of america'];
for (const alpha2 in Countries) {
    names.push(Countries[alpha2 as keyof typeof Countries].name.toLowerCase());
    alpha2List.push(alpha2.toLowerCase());
    alpha3List.push(twoToThree[alpha2 as keyof typeof Countries].toLowerCase());
}

yup.addMethod(yup.string, 'country', function validateCountry() {
    return this.test(
        'is-country',
        ({ value, label }) => `${label} is not a real country (${value})`,
        value => {
            if (!value) {
                return true;
            }

            return (
                alpha3List.includes(value.toLowerCase()) ||
                alpha2List.includes(value.toLowerCase()) ||
                names.includes(value.toLowerCase())
            );
        },
    );
});

/*****************
 * state
 *****************/
const stateNames: string[] = [];
const stateAbbrevs: string[] = [];
for (const i in states) {
    stateNames.push(states[i].name.toLowerCase());
    stateAbbrevs.push(states[i].abbreviation.toLowerCase());
}

yup.addMethod(yup.string, 'state', function validateState(countryKey: string = 'country') {
    return this.when(countryKey, {
        is: (country: string | null) => isUSA(country),
        then: schema =>
            schema.test(
                'is-state',
                ({ value, label }) => `${label} is not a valid US state (${value})`,
                value => {
                    if (!value) {
                        return true;
                    }

                    return (
                        stateNames.includes(value.toLowerCase()) ||
                        stateAbbrevs.includes(value.toLowerCase())
                    );
                },
            ),
    });
});

/*****************
 * postalCode
 *****************/

const postalCodeValidator = (value: string): [boolean, string] => {
    if (!new RegExp(/^[0-9-]+$/).test(value)) {
        return [false, '{label} can only contain numbers and dashes'];
    }

    if (value.length < 5) {
        return [false, '{label} must have at least 5 digits'];
    }

    return [true, ''];
};

yup.addMethod(yup.string, 'postalCode', function validatePostalCode() {
    return this.test(
        'is-valid-postal-code',
        ({ value, label }) => postalCodeValidator(value)[1].replaceAll('{label}', label),
        value => {
            // ignore if no value provided (required vs nullable rules will pick up here)
            if (!value) {
                return true;
            }

            return postalCodeValidator(value)[0];
        },
    );
});

export type { DateTestOptions };
export { yup, firstValidDate };
